import { useCallback, useEffect, useState } from "react";
import { requireSupabase } from "../lib/supabase";

type Person = {
  email: string;
  full_name: string | null;
  role: string;
  status: "pending" | "approved" | "invited" | "removed";
};

const statusLabels = { pending: "Pending approval", approved: "Has access", invited: "Ready to sign in", removed: "Access removed" };

export function PeopleView() {
  const [people, setPeople] = useState<Person[]>([]);
  const [email, setEmail] = useState("");
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  const refresh = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data, error } = await requireSupabase().rpc("portal_list_people");
      if (error) throw error;
      setPeople(data ?? []);
    } catch (e) {
      setError(e instanceof Error ? e.message : (e as { message?: string })?.message || "Could not load people. Please try again.");
      setPeople([]);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { void refresh(); }, [refresh]);

  async function changeAccess(address: string, approved: boolean) {
    if (busy) return;
    if (!approved && !window.confirm(`Remove portal access for ${address}?`)) return;
    setBusy(true);
    setError("");
    setMessage("");
    try {
      const { error } = await requireSupabase().rpc("portal_set_person_access", { p_email: address, p_approved: approved });
      if (error) throw error;
      setEmail("");
      setMessage(approved ? `${address} can now sign in with Google. No invitation email was sent.` : `Portal access removed for ${address}.`);
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : (e as { message?: string })?.message || "Could not save access. Please try again.");
    } finally { setBusy(false); }
  }

  const pending = people.filter((person) => person.status === "pending").length;
  const shown = people.filter((person) => (filter === "all" || person.status === filter)
    && `${person.email} ${person.full_name ?? ""}`.toLowerCase().includes(search.toLowerCase()));

  return <div className="page-stack people-view">
    <section className="panel">
      <div className="panel-heading"><div><div className="eyebrow">ADMIN ACCESS</div><h2>Add a person</h2></div></div>
      <p className="muted">Add a RESQ email to give Media Portal viewer access, or approve a Media Portal request below. RESQ Archive access is managed separately.</p>
      <form className="people-add" onSubmit={(event) => { event.preventDefault(); void changeAccess(email.trim().toLowerCase(), true); }}>
        <label className="field"><span>RESQ email address</span><input type="email" required maxLength={254} placeholder="name@resqct.org" value={email} onChange={(event) => setEmail(event.target.value)} disabled={busy} /></label>
        <button className="button primary" disabled={busy || loading} type="submit">{busy ? "Saving…" : "Add person"}</button>
      </form>
      <p className="muted people-note">Viewer access includes the archive and follow-ups. Google Drive file permissions are managed separately.</p>
    </section>
    {error && <div className="alert error" role="alert">{error}</div>}
    {message && <div className="people-notice" role="status">{message}</div>}
    <section className="panel" aria-label="Portal people">
      <div className="panel-heading"><h2>People <small className="muted">({people.length})</small></h2><button className="button secondary" onClick={() => void refresh()} disabled={loading || busy}>Refresh requests</button></div>
      <p className="muted people-note">Only Media Portal users, people you added, and requests submitted here are listed. Signing in to RESQ Archive does not create a request here.</p>
      <div className="people-filters">
        <label className="field"><span>Search people</span><input type="search" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Name or email" /></label>
        <label className="field"><span>Access status</span><select value={filter} onChange={(event) => setFilter(event.target.value)}><option value="all">Everyone</option><option value="pending">Pending approval ({pending})</option><option value="approved">Has access</option><option value="invited">Ready to sign in</option><option value="removed">Access removed</option></select></label>
      </div>
      {loading ? <p role="status">Loading people…</p> : shown.length === 0 ? <p className="muted">{error ? "People could not be loaded." : "No people match this view."}</p> : <ul className="people-list">
        {shown.map((person) => <li key={person.email} className="people-row">
          <div className="people-identity"><strong>{person.full_name || person.email.split("@")[0]}</strong><span>{person.email}</span></div>
          <div><span className={`people-status ${person.status}`}>{statusLabels[person.status]}</span><small className="people-role">{person.role}</small></div>
          <div className="people-actions">{person.role === "admin" ? <span className="muted">Administrator</span> : person.status === "approved" || person.status === "invited"
            ? <button className="button secondary" disabled={busy} onClick={() => void changeAccess(person.email, false)}>Remove access</button>
            : <button className="button primary" disabled={busy} onClick={() => void changeAccess(person.email, true)}>{person.status === "pending" ? "Approve request" : "Restore access"}</button>}</div>
        </li>)}
      </ul>}
    </section>
  </div>;
}
